import DraggableModal from '../../modal/DraggableModal';
import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import {FormLabel, MenuItem} from '@mui/material';
import Grid from '@mui/material/Grid';
import AgyleSelectField from '../../ui/selector/agyleSelectField';
import EditorMarkdownComponent from '../../editor/editorMarkdown';
import AgyleButton from '../../ui/buttons/agyleButton';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useWindowSize} from '../../../util/formatter';
import {clinicalNoteTypeDict} from '../../../store/reducers/dicts';
import {addNoteForPatient, noteType, setNoteType, updateNoteForPatient} from '../../../store/reducers/notes';
import {saveAgylePreference} from '../../../store/reducers/preferences';
import {useClinicalNoteStyles} from '../clinicalNotes';
import {PATIENT_CONTEXT_TYPE} from '../../../util/patientContextUtil';

const NewNoteDraggableModal = ({
                                   open,
                                   onClose,
                                   patient,
                                   patientType,
                                   noteActive,
                                   disableSave,
                                   clinicNotesWindowSize,
                                   setDefaultNoteType,
                                   showAllNotes,
                                   customNoteTextBounds,
                               }) => {
    const classes = useClinicalNoteStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const [windowWidth, windowHeight] = useWindowSize();
    const noteTextWidth = windowWidth / 3 < 300 ? 450 : ((windowWidth / 4 > 1000) ? 1000 : windowWidth / 2.4);
    const noteTextHeight = windowHeight / 4 > 300 ? 320 : (windowHeight / 4 < 250) ? windowHeight / 5.5 : 260;

    // Selectors
    const clinicalNoteType = useSelector(clinicalNoteTypeDict);
    const type = useSelector(noteType);

    // Draggable modal states
    const noteTextRef = React.useRef(null);
    const [noteTextBounds, setNoteTextBounds] = useState(customNoteTextBounds || {
        left: -(windowWidth - 670),
        right: 0,
        top: 0,
        bottom: (windowHeight / 3),
    });

    const [availableActionToRegister, setAvailableActionToRegister] = React.useState([]);

    // Note
    const [noteBody, setNoteBody] = useState('');
    const [updateVal, setUpdateVal] = useState(false);

    useEffect(() => {
        if (open) {
            if (noteActive?.note) {
                setNoteBody(noteActive?.note || '');
                setUpdateVal(true);
                if (noteActive?.recordType) dispatch(setNoteType(noteActive?.recordType));
            } else {
                setNoteBody('');
            }
        }
    }, [open, noteActive]);

    useEffect(() => {
        recalculateBounds();
        let availableActionToRegisterLocal = [...availableActionToRegister];
        availableActionToRegisterLocal.forEach(action => {
            action.disabled = open;
        });
        setAvailableActionToRegister(availableActionToRegisterLocal);
    }, [open]);

    const recalculateBounds = () => {
        if (clinicNotesWindowSize && noteTextRef && !_.isEmpty(noteTextRef) && !_.isEmpty(noteTextRef.current)) {
            let newBottom = clinicNotesWindowSize.height - noteTextRef.current.offsetHeight;
            let newLeft = -(clinicNotesWindowSize.width - noteTextRef.current.offsetWidth - 10);
            if (noteTextBounds.bottom !== newBottom || noteTextBounds.left !== newLeft) {
                setNoteTextBounds({left: newLeft, right: 0, top: 0, bottom: newBottom});
            }
        }
    };

    const selectOptions = () => {
        let options = [];
        !_.isEmpty(clinicalNoteType) && clinicalNoteType.forEach((cnote) => {
            if ((cnote.edau && patientType === PATIENT_CONTEXT_TYPE.EDAU) || (cnote.ed && patientType !== PATIENT_CONTEXT_TYPE.EDAU))
                options.push(cnote);
        });
        return options;
    };

    const renderCustomOption = (opt) => {
        return (
            <MenuItem
                key={opt?.__code}
                value={opt?.__code}
            >
                {t(opt?.__description)}
            </MenuItem>
        );
    };

    const noteUpdated = (event) => {
        setNoteBody(event.target.value);
    };

    const addUpdateNewNote = (_event, dataOverwrite) => {
        if (noteActive) {
            const noteClone = _.cloneDeep(noteActive);
            noteClone.note = dataOverwrite || noteBody;
            noteClone.recordType = type;
            delete noteClone.__version;
            delete noteClone.__type;
            delete noteClone.__href;
            dispatch(
                updateNoteForPatient(
                    patient.__id,
                    patient.patient.__id,
                    noteClone.__id,
                    noteClone,
                    !showAllNotes,
                ),
            );
        } else {
            let newNote = {
                note: dataOverwrite || noteBody,
                recordType: type,
                patientsInWardsRef: patient.__id ? [patient.__id] : [],
                episodeId: patient?.admission?.consultantEpisodeId || null,
            };
            dispatch(saveAgylePreference({defaultNoteType: type}));
            if (setDefaultNoteType) setDefaultNoteType(type);
            dispatch(
                addNoteForPatient(patient.patient.__id, newNote, patient.__id, false, !showAllNotes),
            );
        }
        handleCloseForm(true);
    };

    const handleChangeNoteType = (value) => {
        dispatch(setNoteType(value));
    };

    const handleCloseForm = (update = false) => {
        setNoteBody('');
        setUpdateVal(false);
        onClose(update);
    };

    if (!open) return open;

    return (
        <DraggableModal
            bounds={noteTextBounds}
            width={noteTextWidth}
            nodeRef={noteTextRef}
            onClose={() => handleCloseForm()}
        >
            <Grid container spacing={1} justifyContent="space-between">
                <Grid item xs={12}>
                    <FormLabel style={{fontWeight: 'bold'}}>
                        {t('Note')}
                    </FormLabel>
                </Grid>

                <Grid item xs={12} style={{display: 'flex', flexDirection: 'column', gap: 5}}>
                    <AgyleSelectField
                        onChange={handleChangeNoteType}
                        label={t('Note Type')}
                        options={selectOptions()}
                        value={type}
                        withBorder
                        withBackground
                        customObject={renderCustomOption}
                        clearButtonVisible
                        defaultClearValue={disableSave ? 'historical' : 'clinical'}
                    />

                    <FormLabel className={classes.noteTitle}>
                        {t('Enter note here')}
                    </FormLabel>

                    <EditorMarkdownComponent
                        labelClass="txtUp"
                        code={noteBody}
                        onChange={noteUpdated}
                        height={noteTextHeight}
                        ignorePaper={true}
                        previewStateChange={recalculateBounds}
                        updateVal={updateVal}
                    />
                </Grid>

                <Grid item container xs={6}>
                    <div style={{flex: '1 0 0'}}>
                        <AgyleButton
                            variant="outlined"
                            onClick={() => handleCloseForm()}
                        >
                            {t('Close')}
                        </AgyleButton>
                    </div>
                </Grid>

                <Grid item container xs={6} justifyContent="flex-end">
                    <AgyleButton
                        color="primary"
                        variant="contained"
                        onClick={addUpdateNewNote}
                        autoFocus
                        disabled={!open}
                    >
                        {t('Submit')}
                    </AgyleButton>
                </Grid>
            </Grid>
        </DraggableModal>
    );
};

export default NewNoteDraggableModal;